import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBooking } from '../../contexts/BookingContext';
import { generateTimeSlots, formatDateShort } from '../../utils/timeUtils';
import type { RescheduleReservationRequest } from '../../types/reservation';

type RescheduleModalProps = {
  isOpen: boolean;
  reservationId: string;
  currentDate: string;
  currentTimeSlots: string[];
  currentFacilityId: string;
  customerName: string;
  onClose: () => void;
  onReschedule: (reservationId: string, data: RescheduleReservationRequest) => Promise<void>;
};

export const RescheduleModal: React.FC<RescheduleModalProps> = ({
  isOpen,
  reservationId,
  currentDate,
  currentTimeSlots,
  currentFacilityId,
  customerName,
  onClose,
  onReschedule,
}) => {
  const { t } = useTranslation();
  const { facilities, fetchFacilityBookings, facilityTimeSlots } = useBooking();

  const [newDate, setNewDate] = useState(currentDate);
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [newFacilityId, setNewFacilityId] = useState(currentFacilityId);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timeSlots = generateTimeSlots(6, 22);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setNewDate(currentDate);
      setSelectedSlots([...currentTimeSlots]);
      setNewFacilityId(currentFacilityId);
      setNotes('');
      setError(null);
    }
    // Re-init only on open transition; dep array intentionally minimal so
    // user edits aren't wiped if parent re-renders with a new array reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Fetch bookings for selected facility and date
  useEffect(() => {
    if (isOpen && newFacilityId && newDate) {
      fetchFacilityBookings(newFacilityId, newDate).catch(console.error);
    }
  }, [isOpen, newFacilityId, newDate, fetchFacilityBookings]);

  if (!isOpen) return null;

  const getSlotStatus = (time: string) => {
    const slots = facilityTimeSlots[newFacilityId]?.[newDate] || [];
    return slots.find(s => s.time === time);
  };

  // Slots that belong to the reservation being rescheduled are editable
  // (deselect to shorten, keep selected to preserve) when the user is still
  // on the original date + facility. The API marks them as isBooked:true
  // because they are booked — by THIS reservation. We override that here.
  const isOwnReservationSlot = (time: string): boolean => {
    return (
      newDate === currentDate &&
      newFacilityId === currentFacilityId &&
      currentTimeSlots.includes(time)
    );
  };

  const isSlotAvailable = (time: string): boolean => {
    if (isOwnReservationSlot(time)) return true;
    const status = getSlotStatus(time);
    return (status?.isAvailable === true) && (status?.isBooked === false);
  };

  const toggleSlot = (time: string) => {
    if (!isSlotAvailable(time)) return;

    setSelectedSlots(prev => {
      if (prev.includes(time)) {
        return prev.filter(t => t !== time);
      } else {
        return [...prev, time].sort();
      }
    });
  };

  const handleSubmit = async () => {
    if (selectedSlots.length === 0) {
      setError(t('reschedule.selectTimeSlotsError') || 'Please select at least one time slot');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onReschedule(reservationId, {
        newDate,
        newTimeSlots: selectedSlots,
        newFacilityId: newFacilityId !== currentFacilityId ? newFacilityId : undefined,
        notes: notes || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('reschedule.error') || 'Failed to reschedule');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get today's date for min date validation
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-brand-primary-deep rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold text-white mb-2">
          {t('reschedule.title') || 'Reschedule Reservation'}
        </h3>
        <p className="text-sm text-white/70 mb-4">
          {t('reschedule.subtitle') || 'Move reservation to a new date/time'}
        </p>

        {/* Current Booking Info */}
        <div className="mb-4 p-3 bg-surface-card rounded-lg">
          <div className="text-sm text-ink-secondary">{t('reschedule.currentBooking') || 'Current Booking'}</div>
          <div className="text-ink-primary font-semibold">{customerName}</div>
          <div className="text-sm text-ink-secondary mt-1">
            {formatDateShort(currentDate)} | {currentTimeSlots.join(', ')}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-brand-warm/20 border border-brand-warm/40 rounded-lg">
            <p className="text-sm text-brand-warm">{error}</p>
          </div>
        )}

        {/* Facility Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-white/70 mb-2">
            {t('reschedule.facility') || 'Facility'}
          </label>
          <select
            value={newFacilityId}
            onChange={(e) => {
              setNewFacilityId(e.target.value);
              setSelectedSlots([]); // Reset slots when facility changes
            }}
            className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
          >
            {facilities.map(facility => (
              <option key={facility.id} value={facility.id}>
                {facility.name}
              </option>
            ))}
          </select>
        </div>

        {/* Date Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-white/70 mb-2">
            {t('reschedule.newDate') || 'New Date'}
          </label>
          <input
            type="date"
            value={newDate}
            min={today}
            onChange={(e) => {
              setNewDate(e.target.value);
              setSelectedSlots([]); // Reset slots when date changes
            }}
            className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
          />
        </div>

        {/* Time Slots Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-white/70 mb-2">
            {t('reschedule.selectTimeSlots') || 'Select New Time Slots'}
          </label>
          <div className="grid grid-cols-6 gap-2 max-h-64 overflow-y-auto p-2 bg-surface-card rounded-lg">
            {timeSlots.map(time => {
              const available = isSlotAvailable(time);
              const selected = selectedSlots.includes(time);
              const status = getSlotStatus(time);

              return (
                <button
                  key={time}
                  type="button"
                  onClick={() => toggleSlot(time)}
                  disabled={!available}
                  className={`px-2 py-2 rounded text-sm font-medium transition-colors ${
                    selected
                      ? 'bg-brand-primary text-white'
                      : available
                      ? 'bg-brand-confirm/15 text-brand-confirm-deep hover:bg-brand-confirm/50'
                      : status?.isBooked
                      ? 'bg-surface-app-elevated text-ink-muted cursor-not-allowed'
                      : 'bg-surface-app-elevated text-ink-muted cursor-not-allowed'
                  }`}
                  title={
                    !available
                      ? status?.isBooked
                        ? t('reschedule.slotBooked') || 'Already booked'
                        : t('reschedule.slotNotAvailable') || 'Not available'
                      : selected
                      ? t('reschedule.slotSelected') || 'Selected'
                      : t('reschedule.slotAvailable') || 'Available'
                  }
                >
                  {time}
                </button>
              );
            })}
          </div>
          {selectedSlots.length > 0 && (
            <div className="mt-2 text-sm text-white/70">
              {t('reschedule.selectedSlots') || 'Selected'}: {selectedSlots.join(', ')}
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-white/70 mb-2">
            {t('reschedule.notes') || 'Reason for Rescheduling'} ({t('common.optional') || 'Optional'})
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
            placeholder={t('reschedule.notesPlaceholder') || 'e.g., Customer requested change...'}
            rows={2}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
          >
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || selectedSlots.length === 0}
            className="flex-1 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting
              ? t('reschedule.rescheduling') || 'Rescheduling...'
              : t('reschedule.rescheduleButton') || 'Reschedule'}
          </button>
        </div>
      </div>
    </div>
  );
};
